/**
 * useWebRTC
 *
 * Manages the full WebRTC peer connection lifecycle:
 *  - Creating offers / answers
 *  - ICE candidate exchange
 *  - Local + remote media streams
 *  - Signalling via the WebSocket send() helper
 *
 * The hook is intentionally transport-agnostic — it only needs a
 * `sendSignal` function and a message dispatcher (`handleMessage`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { WsMessage } from './useWebSocket';

export type CallState =
  | 'idle'
  | 'calling'       // we placed the call, waiting for answer
  | 'ringing'       // we're receiving an incoming call
  | 'active'        // call is live
  | 'ended';

export interface UseWebRTCOptions {
  localUserId: string;
  /** send() from useWebSocket */
  sendSignal: (msg: WsMessage) => void;
  /** Called when the call is answered (active) */
  onCallActive?: (callLogId: string) => void;
  /** Called when the call ends */
  onCallEnded?: (callLogId: string) => void;
}

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  // Add TURN servers here for production:
  // { urls: 'turn:your-turn-server.com', username: '...', credential: '...' }
];

export function useWebRTC({
  localUserId,
  sendSignal,
  onCallActive,
  onCallEnded,
}: UseWebRTCOptions) {
  const pcRef           = useRef<RTCPeerConnection | null>(null);
  const localStreamRef  = useRef<MediaStream | null>(null);
  const callLogIdRef    = useRef<string | null>(null);
  const remoteUserRef   = useRef<string | null>(null);

  const [callState, setCallState]           = useState<CallState>('idle');
  const [localStream, setLocalStream]       = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream]     = useState<MediaStream | null>(null);
  const [incomingCall, setIncomingCall]     = useState<{ from: string; callLogId: string; hasVideo: boolean } | null>(null);
  const [isMuted, setIsMuted]               = useState(false);
  const [isCameraOff, setIsCameraOff]       = useState(false);

  // ── Peer connection helpers ──────────────────────────────────────

  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = ({ candidate }) => {
      if (candidate && remoteUserRef.current) {
        sendSignal({ type: 'ice-candidate', to: remoteUserRef.current, candidate: candidate.toJSON() });
      }
    };

    pc.ontrack = (evt) => {
      setRemoteStream(evt.streams[0] ?? null);
    };

    pc.onconnectionstatechange = () => {
      if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
        hangUp(true);
      }
    };

    pcRef.current = pc;
    return pc;
  }, [sendSignal]);

  const getLocalMedia = useCallback(async (video: boolean): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video });
    localStreamRef.current = stream;
    setLocalStream(stream);
    return stream;
  }, []);

  const addTracksToPC = (pc: RTCPeerConnection, stream: MediaStream) => {
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));
  };

  // ── Outbound call ────────────────────────────────────────────────

  const startCall = useCallback(
    async (toUserId: string, hasVideo = true) => {
      if (callState !== 'idle') return;

      remoteUserRef.current = toUserId;
      setCallState('calling');

      try {
        // 1. Notify callee FIRST — before touching media so they start
        //    ringing even while we're waiting for camera/mic permission.
        sendSignal({ type: 'call-start', to: toUserId, hasVideo });

        // 2. Request local media (triggers permission prompt on mobile)
        const pc     = createPeerConnection();
        const stream = await getLocalMedia(hasVideo);
        addTracksToPC(pc, stream);

        // 3. Create and send offer
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSignal({ type: 'offer', to: toUserId, sdp: offer });
      } catch (err) {
        console.error('startCall failed:', err);
        // Clean up so UI resets to idle
        localStreamRef.current?.getTracks().forEach((t) => t.stop());
        pcRef.current?.close();
        pcRef.current = null;
        localStreamRef.current = null;
        setCallState('ended');
        sendSignal({ type: 'call-end', to: toUserId, callLogId: callLogIdRef.current });
        setTimeout(() => setCallState('idle'), 1500);
      }
    },
    [callState, createPeerConnection, getLocalMedia, sendSignal]
  );

  // ── Incoming call handling ───────────────────────────────────────

  const answerCall = useCallback(
    async () => {
      if (!incomingCall) return;
      const { from, callLogId, hasVideo } = incomingCall;

      remoteUserRef.current = from;
      callLogIdRef.current  = callLogId;
      setIncomingCall(null);
      setCallState('active');

      const pc     = createPeerConnection();
      const stream = await getLocalMedia(hasVideo);
      addTracksToPC(pc, stream);

      sendSignal({ type: 'call-answer', to: from, callLogId });
    },
    [incomingCall, createPeerConnection, getLocalMedia, sendSignal]
  );

  const rejectCall = useCallback(() => {
    if (!incomingCall) return;
    sendSignal({ type: 'call-reject', to: incomingCall.from, callLogId: incomingCall.callLogId });
    setIncomingCall(null);
  }, [incomingCall, sendSignal]);

  // ── Hang up ──────────────────────────────────────────────────────

  const hangUp = useCallback(
    (silent = false) => {
      const remoteUser  = remoteUserRef.current;
      const callLogId   = callLogIdRef.current;

      // Stop local tracks
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;

      // Close peer connection
      pcRef.current?.close();
      pcRef.current = null;

      setLocalStream(null);
      setRemoteStream(null);
      setCallState('ended');

      if (!silent && remoteUser) {
        sendSignal({ type: 'call-end', to: remoteUser, callLogId });
      }

      if (callLogId) onCallEnded?.(callLogId);

      remoteUserRef.current = null;
      callLogIdRef.current  = null;

      // Auto-reset to idle after a short delay
      setTimeout(() => setCallState('idle'), 2000);
    },
    [sendSignal, onCallEnded]
  );

  // ── Media controls ───────────────────────────────────────────────

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => { t.enabled = !t.enabled; });
    setIsMuted((m) => !m);
  }, []);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    setIsCameraOff((off) => !off);
  }, []);

  // ── Inbound signalling dispatcher ───────────────────────────────

  const handleMessage = useCallback(
    async (msg: WsMessage) => {
      switch (msg.type) {

        case 'incoming-call': {
          setIncomingCall({
            from:      msg.from as string,
            callLogId: msg.callLogId as string,
            hasVideo:  msg.hasVideo as boolean,
          });
          setCallState('ringing');
          break;
        }

        case 'call-initiated': {
          callLogIdRef.current = msg.callLogId as string;
          break;
        }

        case 'call-answered': {
          callLogIdRef.current = msg.callLogId as string;
          setCallState('active');
          onCallActive?.(msg.callLogId as string);
          break;
        }

        case 'call-rejected':
        case 'call-ended': {
          hangUp(true);
          break;
        }

        case 'offer': {
          // We receive an offer — create peer connection and answer
          const pc = pcRef.current ?? createPeerConnection();
          remoteUserRef.current = msg.from as string;

          const stream = await getLocalMedia(true);
          addTracksToPC(pc, stream);

          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp as RTCSessionDescriptionInit));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendSignal({ type: 'answer', to: msg.from as string, sdp: answer });
          setCallState('active');
          break;
        }

        case 'answer': {
          const pc = pcRef.current;
          if (!pc) break;
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp as RTCSessionDescriptionInit));
          setCallState('active');
          if (callLogIdRef.current) onCallActive?.(callLogIdRef.current);
          break;
        }

        case 'ice-candidate': {
          const pc = pcRef.current;
          if (!pc || !msg.candidate) break;
          try {
            await pc.addIceCandidate(new RTCIceCandidate(msg.candidate as RTCIceCandidateInit));
          } catch (e) {
            console.warn('Failed to add ICE candidate', e);
          }
          break;
        }
      }
    },
    [createPeerConnection, getLocalMedia, hangUp, onCallActive, sendSignal]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      pcRef.current?.close();
    };
  }, []);

  return {
    callState,
    localStream,
    remoteStream,
    incomingCall,
    isMuted,
    isCameraOff,
    startCall,
    answerCall,
    rejectCall,
    hangUp,
    toggleMute,
    toggleCamera,
    handleMessage,
  };
}
